import React, { ReactNode, useEffect } from "react";
import KogAccordion from "@/main/components/shared/accordion/accordion";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { useDispatch } from "react-redux";
import { KeyValue } from "@/main/models/common";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KOGPaginationGrid from "@/main/components/shared/paginationGrid/paginationGrid";
import { getQuoteImportStatus, useQuoteImportStatusReducer } from "@/main/store/quoteImportStatus";
import { getTranslateByKey, isNullUndefinedEmpty } from "@/main/services/utils";
import { quoteImportApi } from "@/main/api";
import { ImportQuoteFileHistoryRequest } from "@/main/models/request";
import { ImportQuoteFileHistoryResponse } from "@/main/models/response";
import FileHistoryRow from "./fileHistoryRow/fileHistoryRow";
import * as S from "./styles";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

export interface IFileHistory {
	id: string;
	name: string;
}

const DEBOUNCE_TIME = 500;
const INITIAL_FORM_VALUES = {
	status: null as KeyValue | null,
	fileName: "",
	importModal: true,
};

export type IFormValues = typeof INITIAL_FORM_VALUES;

interface IFileHistoryGrid {
	isOpenModal: boolean;
	formFiltersFileHistory: ImportQuoteFileHistoryRequest | null;
	dataFileHistory: ImportQuoteFileHistoryResponse[];
	setDataFileHistory: React.Dispatch<React.SetStateAction<ImportQuoteFileHistoryResponse[]>>;
	getDataOrderHistory: (filters?: ImportQuoteFileHistoryRequest) => void;
	children?: ReactNode;
}
function FileHistoryGrid({
	formFiltersFileHistory,
	dataFileHistory,
	setDataFileHistory,
	getDataOrderHistory,
}: IFileHistoryGrid) {
	// Constants
	const ID_SCROLLABLE_TARGET = "id-scrollable-target-history";

	// Hooks
	const { quoteImportStatus: quoteImportStatusReducer } = useQuoteImportStatusReducer();
	const dispatch = useDispatch();

	const { control, getValues } = useFormQueryParams<IFormValues>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_VALUES,
		autoSearchByUrl: true,
		onSubmit: handleChange,
	});
	// States

	// Async Functions
	async function fetchDataFileHistory(request: ImportQuoteFileHistoryRequest) {
		return quoteImportApi.getImportHistory(request).then((response) => response.data);
	}

	// Handle Functions
	function handleChange() {
		const request: ImportQuoteFileHistoryRequest = {};

		const values = getValues();
		if (values.status) request.idQuoteImportStatus = String(values.status?.key);
		if (!isNullUndefinedEmpty(values.fileName)) request.fileName = String(values.fileName);

		getDataOrderHistory(request);
	}

	function createChangeHandlerWithDebouce() {
		let previousChangeTimer: NodeJS.Timeout | null = null;
		return () => {
			if (previousChangeTimer) {
				clearTimeout(previousChangeTimer);
				previousChangeTimer = null;
			}
			previousChangeTimer = setTimeout(handleChange, DEBOUNCE_TIME);
		};
	}

	useEffect(() => {
		if (quoteImportStatusReducer.data === null) {
			dispatch(getQuoteImportStatus());
		}
		const values = getValues();
		if (values.status == null && isNullUndefinedEmpty(values.fileName)) handleChange();
	}, []);

	const rowsHeader = [getTranslateByKey("information.file"), ""];

	return (
		<S.Container>
			<KogInputTextControlledNew
				name="fileName"
				label={getTranslateByKey("information.filename")}
				control={control}
				id="id-importQuote-fileName"
				onChangeCustom={createChangeHandlerWithDebouce()}
			/>
			<KogAutoCompleteControlled
				id="id-importQuote-dropdown-status"
				nameCustom="status"
				label={getTranslateByKey("information.status")}
				control={control}
				options={quoteImportStatusReducer.data}
				isLoading={quoteImportStatusReducer.isLoading}
				onChangeCustom={handleChange}
			/>
			<S.ContainerPagination id={ID_SCROLLABLE_TARGET}>
				<KOGPaginationGrid
					fetchEqualFilters
					idScrollableTarget={ID_SCROLLABLE_TARGET}
					dataGrid={dataFileHistory}
					formFilters={formFiltersFileHistory}
					fetchMore={fetchDataFileHistory}
					setDataGrid={setDataFileHistory}
				>
					<BoxGrid rowHeaders={rowsHeader} marginCustom="0">
						{dataFileHistory.map((file) => (
							<FileHistoryRow key={file.guid} file={file} />
						))}
					</BoxGrid>
				</KOGPaginationGrid>
			</S.ContainerPagination>
		</S.Container>
	);
}

function FileHistoryAccordion(props: IFileHistoryGrid) {
	return (
		<KogAccordion
			header={<h5>{getTranslateByKey("information.importHistory")}</h5>}
			content={<FileHistoryGrid {...props} />}
			propsRootAccordion={{ defaultExpanded: true }}
		/>
	);
}

export default withQueryparams(FileHistoryAccordion, {
	customName: "importQuote",
});
